/**
 * Kolectra Challenge state machine.
 *
 * See AGENT.md §7 for the conceptual overview and §16 for the offline-capable
 * subset. The legal transitions table here is the single source of truth —
 * both client (apps/desktop) and server (apps/api) consume it.
 *
 * Mutations to Challenge.state must go through ChallengeStateService.transition()
 * which validates against this table, enforces permissions, runs guards, writes
 * an AuditEvent, and fires side effects.
 */

import { Permission, type PermissionAtom } from './permissions.js';

export const ChallengeState = {
  DRAFT: 'DRAFT',
  READY_TO_RECORD: 'READY_TO_RECORD',
  RECORDING: 'RECORDING',
  READY_TO_TEST: 'READY_TO_TEST',
  TESTED: 'TESTED',
  JUDGED: 'JUDGED',
  AWAITING_SECOND_REVIEW: 'AWAITING_SECOND_REVIEW',
  DISPUTED: 'DISPUTED',
  APPROVED: 'APPROVED',
} as const;

export type ChallengeStateName = (typeof ChallengeState)[keyof typeof ChallengeState];

export const TERMINAL_STATES: readonly ChallengeStateName[] = [
  ChallengeState.APPROVED,
];

export interface TransitionDef {
  from: ChallengeStateName;
  to: ChallengeStateName;
  /** Permission required by the actor performing the transition. */
  permission: PermissionAtom;
  /**
   * Additional permissions that may also perform this transition (any-of with
   * `permission`). Lets a reviewer drive an edge a worker normally owns — e.g. a
   * QC reviewer publishing a design (`can_force_rework`) or approving a recording
   * (`can_reject_recording`) — without granting them the worker's full role.
   */
  altPermissions?: readonly PermissionAtom[];
  /** True if the transition itself requires network connectivity. */
  onlineRequired: boolean;
  /** Human-readable name used in audit log + UI. */
  name: string;
}

/**
 * The legal transitions table. Adding a row enables a new transition;
 * removing one disables it.
 *
 * Rework path: any non-terminal state → DRAFT requires `can_force_rework`
 * and is handled by a separate transition handler (see ChallengeStateService).
 */
export const LEGAL_TRANSITIONS: readonly TransitionDef[] = [
  {
    from: ChallengeState.DRAFT,
    to: ChallengeState.READY_TO_RECORD,
    permission: Permission.CAN_DESIGN,
    // A reviewer can publish a design they've approved ("Approve Segment").
    altPermissions: [Permission.CAN_FORCE_REWORK],
    onlineRequired: false,
    name: 'submit',
  },
  {
    from: ChallengeState.READY_TO_RECORD,
    to: ChallengeState.RECORDING,
    permission: Permission.CAN_RECORD,
    onlineRequired: false,
    name: 'claim',
  },
  {
    from: ChallengeState.RECORDING,
    to: ChallengeState.READY_TO_TEST,
    permission: Permission.CAN_RECORD,
    // A reviewer approves the recording after listening ("Approve Audio").
    altPermissions: [Permission.CAN_REJECT_RECORDING],
    onlineRequired: false,
    name: 'confirm-recording',
  },
  {
    from: ChallengeState.READY_TO_TEST,
    to: ChallengeState.RECORDING,
    permission: Permission.CAN_REJECT_RECORDING,
    onlineRequired: false,
    name: 'reject-recording',
  },
  {
    from: ChallengeState.READY_TO_TEST,
    to: ChallengeState.TESTED,
    permission: Permission.CAN_RUN_TEST,
    onlineRequired: true, // locked-in decision: hard-block offline tests
    name: 'run-test',
  },
  {
    from: ChallengeState.TESTED,
    to: ChallengeState.JUDGED,
    permission: Permission.CAN_JUDGE,
    onlineRequired: false,
    name: 'judge',
  },
  {
    from: ChallengeState.JUDGED,
    to: ChallengeState.APPROVED,
    permission: Permission.CAN_APPROVE,
    onlineRequired: false,
    name: 'approve-single-rater',
  },
  {
    from: ChallengeState.JUDGED,
    to: ChallengeState.AWAITING_SECOND_REVIEW,
    permission: Permission.CAN_JUDGE,
    onlineRequired: false,
    name: 'route-to-second-review',
  },
  {
    from: ChallengeState.AWAITING_SECOND_REVIEW,
    to: ChallengeState.APPROVED,
    permission: Permission.CAN_APPROVE,
    onlineRequired: false,
    name: 'approve-multi-rater',
  },
  {
    from: ChallengeState.AWAITING_SECOND_REVIEW,
    to: ChallengeState.DISPUTED,
    permission: Permission.CAN_JUDGE,
    onlineRequired: false,
    name: 'dispute',
  },
  {
    from: ChallengeState.DISPUTED,
    to: ChallengeState.APPROVED,
    permission: Permission.CAN_TIEBREAK,
    onlineRequired: false,
    name: 'tiebreak',
  },
];

/**
 * Look up a transition. Returns undefined if the edge is not legal.
 */
export const findTransition = (
  from: ChallengeStateName,
  to: ChallengeStateName,
): TransitionDef | undefined =>
  LEGAL_TRANSITIONS.find((t) => t.from === from && t.to === to);

export const isLegalTransition = (
  from: ChallengeStateName,
  to: ChallengeStateName,
): boolean => findTransition(from, to) !== undefined;

export const isTerminal = (state: ChallengeStateName): boolean =>
  TERMINAL_STATES.includes(state);

/**
 * Rework: any non-terminal state can return to DRAFT with `can_force_rework`
 * and a comment. Handled by a dedicated method on the state service.
 */
export const canRework = (from: ChallengeStateName): boolean =>
  !isTerminal(from);

/**
 * Returns true if the given transition can be initiated offline.
 * The client uses this to disable buttons that require connectivity.
 */
export const isTransitionOfflineCapable = (
  from: ChallengeStateName,
  to: ChallengeStateName,
): boolean => {
  const t = findTransition(from, to);
  return t !== undefined && !t.onlineRequired;
};
